import React, { useState, useRef, useEffect } from "react";
import { ContentItem } from "@/types/content";
import { ContentIcon } from "./ContentIcon";
import { ContentThumbnail } from "./ContentThumbnail";
import { formatContentTime } from "@/utils/formatContentTime";
import { ContentActionsMenu } from "./ContentActionsMenu";
import { Lock, Globe, Pencil, Check, Loader2 } from "lucide-react";
import { Room } from "@/types/rooms";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTranslation } from "@/context/LanguageContext";
export const ContentCard = ({
  item,
  href,
  rooms,
  onAddToRoom,
  onShare,
  onDelete,
  onTitleUpdate,
  onCardClick,
  isLoading,
}: {
  item: ContentItem;
  href: string;
  rooms?: Room[];
  onAddToRoom?: (item: ContentItem, roomId: string) => void;
  onShare?: (item: ContentItem) => void;
  onDelete?: (item: ContentItem) => void;
  onTitleUpdate?: (item: ContentItem, newTitle: string) => void;
  onCardClick?: (item: ContentItem) => void;
  isLoading?: boolean;
}) => {
  const { t } = useTranslation();
  const time = formatContentTime(item.createdAt);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(item.title);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    setEditTitle(item.title);
  }, [item.title]);

  const handleStartEdit = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleSave = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    const trimmedTitle = editTitle.trim();
    if (!trimmedTitle || trimmedTitle === item.title) {
      setIsEditing(false);
      setEditTitle(item.title);
      return;
    }

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from("content")
        .update({ title: trimmedTitle })
        .eq("id", item.id);

      if (error) throw error;

      onTitleUpdate?.(item, trimmedTitle);
      toast.success("Title updated");
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to update title:", error);
      toast.error("Failed to update title");
      setEditTitle(item.title);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditTitle(item.title);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    }
  };

  const handleBlur = () => {
    // Small delay to allow save button click to register
    setTimeout(() => {
      if (isEditing) {
        handleCancel();
      }
    }, 150);
  };

  const handleClick = (e: React.MouseEvent) => {
    if (isEditing) {
      e.preventDefault();
      return;
    }
    if (onCardClick) {
      e.preventDefault();
      onCardClick(item);
    }
  };

  return (
    <a
      href={href}
      className={`group flex flex-col rounded-2xl border bg-card shadow-sm transition hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-md relative ${isLoading ? 'pointer-events-none' : ''}`}
      onClick={handleClick}
    >
      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center rounded-2xl bg-background/80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span className="text-xs font-medium text-muted-foreground">Adding to library...</span>
          </div>
        </div>
      )}
      <div className="relative w-full overflow-hidden rounded-t-2xl border-b">
        <ContentActionsMenu
          item={item}
          rooms={rooms}
          onAddToRoom={onAddToRoom}
          onShare={onShare}
          onDelete={onDelete}
        />

        <ContentThumbnail
          type={item.type}
          thumbnailUrl={item.thumbnailUrl}
          title={item.title}
        />

        {/* Visibility Badge */}
        <div className="absolute right-10 top-2.5 rounded-full bg-background/80 backdrop-blur-sm px-2 py-1 flex items-center gap-1">
          {item.visibility === "private" ? (
            <>
              <Lock className="h-3 w-3 text-muted-foreground" />
              <span className="text-[10px] font-medium text-muted-foreground">{t("common.private")}</span>
            </>
          ) : (
            <>
              <Globe className="h-3 w-3 text-primary" />
              <span className="text-[10px] font-medium text-primary">{t("common.public")}</span>
            </>
          )}
        </div>
      </div>

      <div className="flex w-full items-start gap-2 px-3 py-2">
        <ContentIcon type={item.type} />
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="flex items-center gap-1">
              <input
                ref={inputRef}
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                onClick={(e) => e.stopPropagation()}
                disabled={isSaving}
                className="flex-1 min-w-0 text-sm font-medium bg-transparent border-b border-primary/50 outline-none text-primary py-0.5 disabled:opacity-50"
                maxLength={100}
              />
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="shrink-0 p-1 rounded hover:bg-primary/10 text-primary transition-colors disabled:opacity-50"
                aria-label="Save title"
              >
                <Check className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1 group/title">
              <p className="truncate text-sm font-medium text-primary/80 group-hover:text-primary flex-1 min-w-0">
                {item.title}
              </p>
              <button
                onClick={handleStartEdit}
                className="shrink-0 p-1 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors opacity-0 group-hover/title:opacity-100"
                aria-label="Edit title"
              >
                <Pencil className="h-3 w-3" />
              </button>
            </div>
          )}
          <p className="text-xs text-muted-foreground/80">{time}</p>
        </div>
      </div>
    </a>
  );
};
